import { useRouter } from 'expo-router';
import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import fetchInitiateChallenge from 'features/auth/resetPassword/utils/fetchInitiateChallenge/fetchInitiateChallenge';
import { InformationScreenTypeEnum } from 'features/otp/information/InformationScreenTypeEnum';
import { useContext, useEffect, useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface Challenge {
  challengeId: string;
  challengeContext: string;
  challengeType: string;
  challengeState: string;
}

export function useInitiateChallenge(allow1FA: boolean, identityId?: string) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const { getVaroSessionId, idToken } =
    useContext<AuthContextProps>(AuthContext);
  const authUrl = useAuthUrl(
    FeatureFlagKey.INITIATE_CHALLENGE_USE_K8S_AUTH_SERVICE,
  );

  const initiateChallenge = async (identityId: string) => {
    setLoading(true);

    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    try {
      const { challenges = null } = await fetchInitiateChallenge(
        authUrl,
        {
          challengeContext: 'RESET_PASSWORD_CONTEXT',
          identityId,
        },
        headers,
      );

      setLoading(false);

      if (challenges === null) {
        router.navigate({
          pathname: '/otp/error',
          params: { screenType: InformationScreenTypeEnum.Error },
        });
      } else {
        return challenges;
      }
    } catch (error) {
      setLoading(false);

      logErrorWithContext('InitiateChallenge error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });
    }
  };

  useEffect(() => {
    async function getChallenges() {
      if (allow1FA && identityId) {
        setChallenges((await initiateChallenge(identityId)) || []);
      }
    }

    void getChallenges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    challenges,
    loading,
  };
}
